import { cloneElement, createContext, type PropsWithChildren, type ReactElement, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

type PortalContextType = HTMLElement | null;

const PortalContext = createContext<PortalContextType>(null);

export const PortalContextProvider = ({ children, host }: PropsWithChildren<{ host: HTMLElement | null }>) => (
    <PortalContext.Provider value={host}>{children}</PortalContext.Provider>
);

export const PortalHost = ({ element, onHost }: { element: ReactElement; onHost: (host: HTMLElement | null) => void }) =>
    cloneElement(element, { ref: onHost });

/**
 * Used to set up the place where the portal content shall be rendered and a context provider for accessing the portal.
 *
 * Note that there is only ONE portal context throughout the whole app!
 */
export const usePortalHost = () => {
    return useState<HTMLElement | null>(null);
};

/**
 * Provides a Portal component that renders its children into the PortalHost created by `usePortalHost`.
 *
 * Note that there is only ONE portal context throughout the whole app!
 */
export const usePortal = () => {
    const host = useContext(PortalContext);

    const Portal = ({ children }: PropsWithChildren) => {
        return host && createPortal(children, host);
    };

    return { Portal };
};
